// Client-side feature flags.
//
// Flags here are BUILD-TIME constants, not remote config: flipping one is a
// code change plus a release. That is deliberate for gating a signup path —
// a remotely-togglable signup would be a config mistake away from opening.
//
// Every flag that gates something a user could otherwise reach by calling
// the API directly MUST have a server-side counterpart. A constant in this
// file only changes what the UI offers; it is not a security boundary.

/**
 * Whether new Scout accounts can be registered.
 *
 * FALSE freezes scout signup:
 *   • RoleSelection shows the Scout card as "Coming soon", disabled.
 *   • AuthScreen drops Scout from the signup persona toggle.
 *   • Nothing writes profiles.role = 'scout'.
 *
 * EXISTING scouts are untouched — login, onboarding completion, the
 * verification gate and every scout feature keep working. This flag gates
 * REGISTRATION only.
 *
 * ⚠️ TO RE-ENABLE, FLIP TWO PLACES:
 *   1. this constant  → true
 *   2. public.scout_signups_enabled() in the database
 *      (supabase/scout_signups_freeze_migration.sql) → return true
 *
 * Flipping only this one re-opens the UI while the server still rejects
 * the write, so a user would get an error at the end of signup. Flipping
 * only the server leaves the UI closed but the API open.
 */
export const SCOUT_SIGNUPS_ENABLED = false;

/** Shown when someone taps the frozen Scout option. */
export const SCOUT_COMING_SOON_MESSAGE =
  "Scout accounts are launching soon. Join as a player or academy for now.";

// ---------------------------------------------------------------------------
// Billing mode
// ---------------------------------------------------------------------------

/**
 * How the app presents paid plans.
 *
 *   'web_info' — Plans and benefits are shown. There is NO purchase UI, no
 *                top-ups, and no link or instruction pointing anywhere
 *                else. Tapping Upgrade records an intent; the rest of the
 *                conversation happens over WhatsApp/email, outside the app.
 *
 *   'play'     — The Google Play / RevenueCat purchase flow. All of that
 *                code stays intact and simply lies dormant while the mode
 *                is 'web_info'.
 */
export type BillingMode = "web_info" | "play";

/**
 * Fallback used until app_config resolves, and permanently if that fetch
 * fails.
 *
 * It fails CLOSED to 'web_info' on purpose: showing an inert plan card to
 * someone who could have purchased is a missed upsell, while showing a
 * purchase button that cannot complete is a broken checkout. The safe
 * default is the one with nothing to break.
 */
export const DEFAULT_BILLING_MODE: BillingMode = "web_info";

/**
 * Copy shown wherever a purchase cannot be started.
 *
 * Deliberately says nothing about WHERE to buy — no website, no browser,
 * no store name. In web_info mode the paywall's own Upgrade → Interested
 * flow is the entire call to action; this string only exists for the
 * dormant 'play' branch's error paths.
 */
export const BILLING_UNAVAILABLE_FALLBACK =
  "Upgrades aren't available on this device right now.";

/** Shown under the button once an upgrade intent has been recorded. */
export const UPGRADE_INTENT_CONFIRMATION =
  "Check your WhatsApp or email — we've sent you the details.";
